import React, { useEffect, useMemo, useState } from "react";

export default function DistrictCheckIn({ token, user }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState({ summary: null, divisions: [] });
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch("https://smartcoreapi.ssfkerala.org/districts/checkin.php", {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          throw new Error("Failed to load district data");
        }
        const json = await res.json();
        if (!active) return;
        setData({ summary: json.summary ?? null, divisions: json.divisions ?? [] });
      } catch (err) {
        if (!active) return;
        setError(err.message || "Failed to load district data");
        setData({ summary: null, divisions: [] });
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();
    return () => {
      active = false;
    };
  }, [token, debouncedSearch, filterStatus]);

  const filteredDivisions = useMemo(() => {
    return data.divisions.filter((division) => {
      if (filterStatus !== "all" && division.status !== filterStatus) return false;
      if (!debouncedSearch) return true;
      const haystack = `${division.orgname}`.toLowerCase();
      return haystack.includes(debouncedSearch.toLowerCase());
    });
  }, [data.divisions, debouncedSearch, filterStatus]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold text-gray-900">Division Check-In Overview</h2>
          <p className="text-sm text-gray-500">
            {user.role_id === 2 ? "Divisions in your district" : "All districts"}
          </p>
          {data.summary && (
            <div className="grid grid-cols-2 gap-3 text-sm text-gray-600 sm:grid-cols-3 lg:grid-cols-6">
              <div>
                <div className="text-xs uppercase text-gray-400">Divisions</div>
                <div className="text-base font-semibold">{data.summary.total_divisions}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-400">Scheduled</div>
                <div className="text-base font-semibold text-blue-600">{data.summary.scheduled}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-400">Open today</div>
                <div className="text-base font-semibold text-green-600">{data.summary.open_today}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-400">Past due</div>
                <div className="text-base font-semibold text-red-600">{data.summary.past_due}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-400">Checked-in today</div>
                <div className="text-base font-semibold text-indigo-600">{data.summary.checked_in_today}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-400">Members checked-in</div>
                <div className="text-base font-semibold text-purple-600">{data.summary.checked_in}</div>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 md:w-80">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search divisions"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">All statuses</option>
            <option value="open_today">Open today</option>
            <option value="scheduled_upcoming">Upcoming</option>
            <option value="past_due">Past due</option>
            <option value="not_scheduled">Not scheduled</option>
          </select>
        </div>
      </div>

      {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

      {loading ? (
        <div className="bg-white py-12 text-center shadow-sm">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
        </div>
      ) : filteredDivisions.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-white py-12 text-center text-gray-500">
          No divisions found.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filteredDivisions.map((division) => {
            const checkinOpen = !!division.checkin_open;
            const totalMembers = division.total_members ?? 0;
            const checkedIn = division.checked_in_count ?? 0;
            const percentage = totalMembers > 0 ? Math.round((checkedIn / totalMembers) * 100) : 0;

            return (
              <div key={division.id} className="rounded-2xl border p-4 shadow-sm bg-white space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">{division.orgname}</div>
                    <div className="text-xs uppercase tracking-wide text-gray-400">Division</div>
                  </div>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      checkinOpen
                        ? "bg-green-100 text-green-700"
                        : division.status === "past_due"
                          ? "bg-red-100 text-red-700"
                          : division.status === "scheduled_upcoming"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {checkinOpen ? "Open today" : division.status.replace("_", " ")}
                  </span>
                </div>

                <div className="text-sm text-gray-600 space-y-1">
                  <div>{`Program date: ${division.program_date || "Not set"}`}</div>
                  <div>{`Members checked in: ${checkedIn} / ${totalMembers}`}</div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                    <div
                      className={`h-full rounded-full ${percentage >= 75 ? "bg-green-500" : "bg-blue-500"}`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                  <div className="rounded-full bg-gray-100 px-3 py-1">Checked today: {division.checked_in_today}</div>
                  <div className="rounded-full bg-gray-100 px-3 py-1">Override: {division.override_enabled ? "Yes" : "No"}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
